import { t } from "i18next";

class DeviceGattApi {
    constructor(device) {
        this.device = device;
        this.services = [];
        this.characteristics = [];
    }

    static GATT_FITNESS_MACHINE_SERVICE_UUID = 0x1826;
    static GATT_TEMPERATURE_UUID = 0x2A6E;
    static GATT_TEMPERATURE_MEASUREMENT_UUID = 0x2A1C;
    static GATT_FITNESS_MACHINE_STATUS_UUID = 0x2ADA;
    static GATT_FITNESS_MACHINE_CONTROL_POINT_UUID = 0x2AD9;
    static GATT_HEAT_INDEX_UUID = 0x2A7A;
    static GATT_ELECTRIC_CURRENT_UUID = 0x2AEE;
    static GATT_VOLTAGE_UUID = 0x2B18;
    static GATT_POWER_UUID = 0x2B05;
    static GATT_CYCLING_POWER_FEATURE_UUID = 0x2A65;
    static GATT_HIGH_VOLTAGE_UUID = 0x2BE0;
    static GATT_ROWER_DATA_UUID = 0x2AD1;
    static GATT_REPORT_MAP_UUID = 0x2A4B;
    static GATT_REPORT_UUID = 0x2A4D;
    static GATT_OBJECT_PROPERTIES_UUID = 0x2AC4;

    static GATT_DEVICE_INFO_SERVICE_UUID = 0x180A;
    static GATT_DEVICE_BATTERY_LEVEL_UUID = 0x2A19;
    static GATT_MANUFACTURER_NAME_UUID = 0x2A29;
    static GATT_MODEL_NUMBER_UUID = 0x2A24;
    static GATT_SERIAL_NUMBER_STRING_UUID = 0x2A25;
    static GATT_SYSTEM_ID_UUID = 0x2A23;
    static GATT_FIRMWARE_REVISION_STRING_UUID = 0x2A26;
    static GATT_HARDWARE_REVISION_STRING_UUID = 0x2A27;
    static GATT_EVENT_STATISTICS_UUID = 0x2AF4;

    static FITNESS_MACHINE_STATUS_IDLE = 0x00;
    static FITNESS_MACHINE_STATUS_HEAT = 0x10;
    static FITNESS_MACHINE_STATUS_SLEEP = 0x20;
    static FITNESS_MACHINE_STATUS_CHARGE = 0x30;
    static FITNESS_MACHINE_STATUS_POW_BANK = 0x40;
    static FITNESS_MACHINE_STATUS_DRYING = 0x50;
    static FITNESS_MACHINE_STATUS_ERROR = 0x60;
    static FITNESS_MACHINE_STATUS_BAT_LOW = 0x70;
    static FITNESS_MACHINE_STATUS_BAT_FULL = 0x80;
    static FITNESS_MACHINE_STATUS_SHUTTING_DOWN = 0x90;

    static FITNESS_MACHINE_CONTROL_POINT_INIT = 0x00;
    static FITNESS_MACHINE_CONTROL_POINT_PWM_100 = 0x10;
    static FITNESS_MACHINE_CONTROL_POINT_PWM_50 = 0x20;
    static FITNESS_MACHINE_CONTROL_POINT_PWM_25 = 0x30;
    static FITNESS_MACHINE_CONTROL_POINT_PWM_0 = 0x40;
    static FITNESS_MACHINE_CONTROL_POINT_SMART_HEAT = 0x50;
    static FITNESS_MACHINE_CONTROL_POINT_SHUTDOWN = 0x60;

    static HEAT_INDEX_HEAT_0 = 0;
    static HEAT_INDEX_MIN_MIN_AVG = 1;
    static HEAT_INDEX_MIN_AVG = 2;
    static HEAT_INDEX_MED_AVG = 3;
    static HEAT_INDEX_MAX_AVG = 4;

    static REPORT_CMD_CLEAR = 2;

    static getFitnessMachineStatusText(status) {
        switch (status) {
            case DeviceGattApi.FITNESS_MACHINE_STATUS_IDLE:
                return t("koo.state.idle");
            case DeviceGattApi.FITNESS_MACHINE_STATUS_HEAT:
                return t("koo.state.heat");
            case DeviceGattApi.FITNESS_MACHINE_STATUS_SLEEP:
                return t("koo.state.sleep");
            case DeviceGattApi.FITNESS_MACHINE_STATUS_CHARGE:
                return t("koo.state.charge");
            case DeviceGattApi.FITNESS_MACHINE_STATUS_POW_BANK:
                return t("koo.state.pow_bank");
            case DeviceGattApi.FITNESS_MACHINE_STATUS_DRYING:
                return t("koo.state.drying");
            case DeviceGattApi.FITNESS_MACHINE_STATUS_ERROR:
                return t("koo.state.error");
            case DeviceGattApi.FITNESS_MACHINE_STATUS_BAT_LOW:
                return t("koo.state.bat_low");
            case DeviceGattApi.FITNESS_MACHINE_STATUS_BAT_FULL:
                return t("koo.state.bat_full");
            case DeviceGattApi.FITNESS_MACHINE_STATUS_SHUTTING_DOWN:
                return t("koo.state.shutting_down");
            default:
                return t("koo.state.unknown");
        }
    }

    static getFitnessMachineControlPointText(controlPoint) {
        switch (controlPoint) {
            case DeviceGattApi.FITNESS_MACHINE_CONTROL_POINT_INIT:
                return t("koo.mode.init");
            case DeviceGattApi.FITNESS_MACHINE_CONTROL_POINT_PWM_100:
                return t("koo.mode.pwm_100");
            case DeviceGattApi.FITNESS_MACHINE_CONTROL_POINT_PWM_50:
                return t("koo.mode.pwm_50");
            case DeviceGattApi.FITNESS_MACHINE_CONTROL_POINT_PWM_25:
                return t("koo.mode.pwm_25");
            case DeviceGattApi.FITNESS_MACHINE_CONTROL_POINT_PWM_0:
                return t("koo.mode.pwm_0");
            case DeviceGattApi.FITNESS_MACHINE_CONTROL_POINT_SMART_HEAT:
                return t("koo.mode.smart_heat");
            case DeviceGattApi.FITNESS_MACHINE_CONTROL_POINT_SHUTDOWN:
                return t("koo.mode.shutdown");
            default:
                return t("koo.mode.unknown");
        }
    }

    static getHeatIndexText(heatIndex) {
        switch (heatIndex) {
            case DeviceGattApi.HEAT_INDEX_HEAT_0:
                return t("koo.heat.heat_0");
            case DeviceGattApi.HEAT_INDEX_MIN_MIN_AVG:
                return t("koo.heat.min_min_avg");
            case DeviceGattApi.HEAT_INDEX_MIN_AVG:
                return t("koo.heat.min_avg");
            case DeviceGattApi.HEAT_INDEX_MED_AVG:
                return t("koo.heat.med_avg");
            case DeviceGattApi.HEAT_INDEX_MAX_AVG:
                return t("koo.heat.max_avg");
            default:
                return t("koo.heat.unknown");
        }
    }

    async getService(serviceUuid) {
        let service = null;
        if (!(serviceUuid in this.services)) {
            service = await this.device.gatt.getPrimaryService(serviceUuid);
            this.services[serviceUuid] = service;
        } else {
            service = this.services[serviceUuid];
        }
        return service;
    }

    async getCharacteristic(service, characteristicUuid) {
        let characteristic = null;
        if (!(characteristicUuid in this.characteristics)) {
            characteristic = await service.getCharacteristic(characteristicUuid);
            this.characteristics[characteristicUuid] = characteristic;
        } else {
            characteristic = this.characteristics[characteristicUuid];
        }
        return characteristic;
    }

    async getCharacteristicValue(service, characteristicUuid, valueType, arrayLength, multiply) {
        try {
            const characteristic = await this.getCharacteristic(service, characteristicUuid);
            const value = await characteristic.readValue();
            const m = multiply || 1;
            switch (valueType) {
                case "int8":
                    return value.getInt8() * m;
                case "uint8":
                    return value.getUint8() * m;
                case "uint8*": // array of uint8
                    if (arrayLength) {
                        return Array.from(Array(arrayLength), (_, i) => value.getUint8(i) * m);
                    } else {
                        console.error("Invalid array length");
                        return null;
                    }
                case "int16":
                    return value.getInt16(0, true) * m; // read as little-endian
                case "uint16":
                    return value.getUint16(0, true) * m; // read as little-endian
                case "int32":
                    return value.getInt32(0, true) * m; // read as little-endian
                case "uint32":
                    return value.getUint32(0, true) * m; // read as little-endian
                case "uint32*": // array of uint32
                    if (arrayLength) {
                        return Array.from(Array(arrayLength), (_, i) => value.getUint32(i * 4, true) * m); // read as little-endian
                    } else {
                        console.error("Invalid array length");
                        return null;
                    }
                case "string":
                    const decoder = new TextDecoder();
                    return decoder.decode(value.buffer);
                default:
                    return value;
            }
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    async readMachineData() {
        const service = await this.getService(DeviceGattApi.GATT_FITNESS_MACHINE_SERVICE_UUID);

        const temperature = await this.getCharacteristicValue(service, DeviceGattApi.GATT_TEMPERATURE_UUID, "int8");
        const temperatureMeasurement = await this.getCharacteristicValue(service, DeviceGattApi.GATT_TEMPERATURE_MEASUREMENT_UUID, "int8");
        const fitnessMachineStatus = await this.getCharacteristicValue(service, DeviceGattApi.GATT_FITNESS_MACHINE_STATUS_UUID, "uint8");
        const fitnessMachineControlPoint = await this.getCharacteristicValue(service, DeviceGattApi.GATT_FITNESS_MACHINE_CONTROL_POINT_UUID, "uint8");
        const heatIndex = await this.getCharacteristicValue(service, DeviceGattApi.GATT_HEAT_INDEX_UUID, "uint8");
        const electricCurrent = await this.getCharacteristicValue(service, DeviceGattApi.GATT_ELECTRIC_CURRENT_UUID, "uint16", null, 0.001);
        const voltage = await this.getCharacteristicValue(service, DeviceGattApi.GATT_VOLTAGE_UUID, "uint16", null, 0.001);
        const power = await this.getCharacteristicValue(service, DeviceGattApi.GATT_POWER_UUID, "uint16", null, 0.001);
        const dutyCycle = await this.getCharacteristicValue(service, DeviceGattApi.GATT_CYCLING_POWER_FEATURE_UUID, "uint8");
        const highVoltage = await this.getCharacteristicValue(service, DeviceGattApi.GATT_HIGH_VOLTAGE_UUID, "uint16", null, 0.001);
        const resistance = await this.getCharacteristicValue(service, DeviceGattApi.GATT_ROWER_DATA_UUID, "uint16", null, 0.001);

        this.machineData = {
            temperature: temperature,
            temperatureMeasurement: temperatureMeasurement,
            fitnessMachineStatus: fitnessMachineStatus,
            fitnessMachineControlPoint: fitnessMachineControlPoint,
            heatIndex: heatIndex,
            electricCurrent: electricCurrent,
            voltage: voltage,
            power: power,
            dutyCycle: dutyCycle,
            highVoltage: highVoltage,
            resistance: resistance
        };

        return this.machineData;
    }

    async updateMachineData() {
        // always update all machine data
        return await this.readMachineData();
    }

    async readDeviceInfo() {
        const service = await this.getService(DeviceGattApi.GATT_DEVICE_INFO_SERVICE_UUID);

        const batteryLevel = await this.getCharacteristicValue(service, DeviceGattApi.GATT_DEVICE_BATTERY_LEVEL_UUID, "uint8");
        const manufacturerName = await this.getCharacteristicValue(service, DeviceGattApi.GATT_MANUFACTURER_NAME_UUID, "string");
        const modelNumber = await this.getCharacteristicValue(service, DeviceGattApi.GATT_MODEL_NUMBER_UUID, "string");
        const systemId = await this.getCharacteristicValue(service, DeviceGattApi.GATT_SYSTEM_ID_UUID, "string");
        // const serialNumberString = await this.getCharacteristicValue(service, DeviceGattApi.GATT_SERIAL_NUMBER_STRING_UUID, "string"); // note: blacklisted UUID in Chrome
        const firmwareRevisionString = await this.getCharacteristicValue(service, DeviceGattApi.GATT_FIRMWARE_REVISION_STRING_UUID, "string");
        const hardwareRevisionString = await this.getCharacteristicValue(service, DeviceGattApi.GATT_HARDWARE_REVISION_STRING_UUID, "string");

        const eventStatistics = await this.getCharacteristicValue(service, DeviceGattApi.GATT_EVENT_STATISTICS_UUID, "uint32*", 4);

        this.deviceInfo = {
            batteryLevel: batteryLevel,
            manufacturerName: manufacturerName,
            modelNumber: modelNumber,
            systemId: systemId,
            // serialNumberString: serialNumberString,
            firmwareRevisionString: firmwareRevisionString,
            hardwareRevisionString: hardwareRevisionString
        };

        if (eventStatistics) {
            this.deviceInfo.eventStatistics = {
                execCounter: eventStatistics[0],
                errorCounter: eventStatistics[1],
                wdgResetCounter: eventStatistics[2],
                rporResetCounter: eventStatistics[3]
            };
        }

        return this.deviceInfo;
    }

    async updateDeviceInfo() {
        if (!this.deviceInfo) {
            // read all device info data
            return await this.readDeviceInfo();
        } else {
            // read only updatable parameters
            const service = await this.getService(DeviceGattApi.GATT_DEVICE_INFO_SERVICE_UUID);

            const batteryLevel = await this.getCharacteristicValue(service, DeviceGattApi.GATT_DEVICE_BATTERY_LEVEL_UUID, "uint8");
            this.deviceInfo.batteryLevel = batteryLevel;

            const eventStatistics = await this.getCharacteristicValue(service, DeviceGattApi.GATT_EVENT_STATISTICS_UUID, "uint32*", 4);
            if (eventStatistics) {
                this.deviceInfo.eventStatistics = {
                    execCounter: eventStatistics[0],
                    errorCounter: eventStatistics[1],
                    wdgResetCounter: eventStatistics[2],
                    rporResetCounter: eventStatistics[3]
                };
            }

            return this.deviceInfo;
        }
    }

    async readObjectProperties() {
        const service = await this.getService(DeviceGattApi.GATT_FITNESS_MACHINE_SERVICE_UUID);
        const objectProperties = await this.getCharacteristicValue(service, DeviceGattApi.GATT_OBJECT_PROPERTIES_UUID, "uint8*", 4);

        if (objectProperties) {
            this.configuration = {
                logOn: (objectProperties[0] & 0x01) !== 0,
                constPwm: (objectProperties[0] & 0x02) !== 0,
                cooldown: (objectProperties[0] & 0x04) !== 0,
                testMode: (objectProperties[0] & 0x08) !== 0,
                dryingTimeSec: (objectProperties[1] | (objectProperties[2] << 8))
            }

            return this.configuration;
        }
    }

    async updateObjectProperties() {
        if (!this.configuration) {
            // read all object properties
            return await this.readObjectProperties();
        } else {
            // no need to update
            return this.configuration;
        }
    }

    async changeObjectProperties(configuration) {
        // Prepare config flags
        let configFlags = 0x00;
        if (configuration.logOn) {
            configFlags |= 0x01;
        }
        if (configuration.constPwm) {
            configFlags |= 0x02;
        }
        if (configuration.cooldown) {
            configFlags |= 0x04;
        }
        if (configuration.testMode) {
            configFlags |= 0x08;
        }

        // Prepare configuration array
        const objectProperties = new Uint8Array(4);
        objectProperties[0] = configFlags & 0xFF;
        objectProperties[1] = configuration.dryingTimeSec & 0xFF;
        objectProperties[2] = (configuration.dryingTimeSec >> 8) & 0xFF;
        objectProperties[3] = 0x00;

        const service = await this.getService(DeviceGattApi.GATT_FITNESS_MACHINE_SERVICE_UUID);
        const objectPropertiesCharacteristic = await service.getCharacteristic(DeviceGattApi.GATT_OBJECT_PROPERTIES_UUID);
        await objectPropertiesCharacteristic.writeValue(objectProperties);
    }

    async readReports(handleProgress) {
        const service = await this.getService(DeviceGattApi.GATT_FITNESS_MACHINE_SERVICE_UUID);

        // Read total number of reports, this reading will also reset log reading in the device
        const reportMapBuf = await this.getCharacteristicValue(service, DeviceGattApi.GATT_REPORT_MAP_UUID, "uint8*", 4);
        if (!reportMapBuf) {
            throw new Error("Failed to read report map");
        }

        const chunkCount = reportMapBuf[0] | (reportMapBuf[1] << 8);
        const chunkSize = reportMapBuf[2];

        // Prepare buffer
        let buf = new Uint8Array(chunkCount * chunkSize);

        // Read chunks
        for (let i = 0; i < chunkCount; i++) {
            const reportValue = await this.getCharacteristicValue(service, DeviceGattApi.GATT_REPORT_UUID, "uint8*", 4 + chunkSize); // 4 - offset
            if (!reportValue) {
                throw new Error("Failed to read report");
            }

            const offset = reportValue[0] | (reportValue[1] << 8) | (reportValue[2] << 16) | (reportValue[3] << 24);
            if (offset + chunkSize > buf.length) {
                throw new Error("Invalid report offset");
            }
            buf.set(reportValue.slice(4), offset);

            if (handleProgress) {
                handleProgress(i, chunkCount);
            }
        }

        return buf;
    }

    async clearReports() {
        const service = await this.getService(DeviceGattApi.GATT_FITNESS_MACHINE_SERVICE_UUID);
        const reportMapCharacteristic = await service.getCharacteristic(DeviceGattApi.GATT_REPORT_MAP_UUID);

        const cmd = new Uint8Array([DeviceGattApi.REPORT_CMD_CLEAR, 0]);
        await reportMapCharacteristic.writeValue(cmd);
    }

    async readAllData() {
        await this.readMachineData();
        await this.readDeviceInfo();
        await this.readObjectProperties();
        return {
            machineData: this.machineData,
            deviceInfo: this.deviceInfo,
            configuration: this.configuration
        };
    }

    async updateAllData() {
        await this.updateMachineData();
        await this.updateDeviceInfo();
        await this.updateObjectProperties();
        return {
            machineData: this.machineData,
            deviceInfo: this.deviceInfo,
            configuration: this.configuration
        };
    }

    async changeControlPoint(controlPoint) {
        const service = await this.getService(DeviceGattApi.GATT_FITNESS_MACHINE_SERVICE_UUID);
        const controlPointCharacteristic = await service.getCharacteristic(DeviceGattApi.GATT_FITNESS_MACHINE_CONTROL_POINT_UUID);

        const value = new Uint8Array([controlPoint]);
        await controlPointCharacteristic.writeValue(value);
    }

    static isCharging(data) {
        return data.machineData?.fitnessMachineStatus === DeviceGattApi.FITNESS_MACHINE_STATUS_CHARGE;
    }

    static getAllServices() {
        return [
            this.GATT_FITNESS_MACHINE_SERVICE_UUID,
            this.GATT_DEVICE_INFO_SERVICE_UUID
        ];
    }
}

export { DeviceGattApi };
